/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../redux/hooks";
import { setActiveTab } from "../../../redux/slice/activeTabSlice";
import { getPlayerImageUrl, isTestMatch } from "../../../helper/helper";
import { buildMatchVm, type MatchVm, type TeamVm } from "../../tournamentPage/matchVm";
import { IcArr, IcCalL, IcChk, IcPin, IcPlay, IcTrophy, IcLive } from "../../tournamentPage/icons";
import { useLeagueMatches } from "./useLeagueMatches";

/** Results shown after the live and upcoming cards. */
const MAX_RESULTS = 10;

const IcPrev = () => (<svg width="18" height="18" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>);
const IcNext = () => (<svg width="18" height="18" viewBox="0 0 16 16" fill="none"><path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>);
const IcTrS = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M7 4h10v5a5 5 0 01-10 0V4zM7 6H4v1.5A3.5 3.5 0 007.5 11M17 6h3v1.5a3.5 3.5 0 01-3.5 3.5M12 14v3.5M8.5 20h7M9.5 17.5h5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>);

type Card = MatchVm & { tournamentId?: string; tournamentName: string; format: string; stage: string | null };

/** "T20", "T10", "One-day", "Multi-day"... from the match's own tournament. */
const formatOf = (raw: any) => {
  const t = raw.tournament ?? {};
  const type = (t.matchType ?? raw.matchType ?? "").toUpperCase();
  const overs = t.maxOvers ?? raw.matchOvers;
  if (isTestMatch(type)) return raw.scheduledDays > 1 ? `${raw.scheduledDays}-day` : "Multi-day";
  if (type === "ODI" || overs === 50) return "One-day";
  if (overs) return `T${overs}`;
  return "";
};

const stageOf = (playoffType?: string | null) => {
  const k = (playoffType ?? "").toUpperCase();
  if (!k || k === "LEAGUE_MATCH") return null;
  if (k === "FINAL") return "FINAL";
  const m = k.match(/^(SEMI_FINAL_QUALIFIER|SEMI_FINAL|QUARTER_FINAL|ELIMINATOR|QUALIFIER)(?:_(\d+))?$/);
  return m ? m[1].replace(/_/g, "-").replace("SEMI-FINAL-QUALIFIER", "SEMI-FINAL QUALIFIER") : null;
};

const toCard = (raw: any, bucket: MatchVm["bucket"]): Card => ({
  ...buildMatchVm(raw, bucket),
  tournamentId: raw.tournament?.tournamentId,
  tournamentName: raw.tournament?.tournamentName ?? raw.leagueName ?? "",
  format: formatOf(raw),
  stage: stageOf(raw.playoffType),
});

const Crest = ({ t }: { t: TeamVm }) => (
  <span className="cr" style={{ background: t.color, overflow: "hidden" }}>
    {t.image ? <img src={getPlayerImageUrl(t.image) ?? ""} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : t.code}
  </span>
);

/** Multi-innings scores ("149/10 & 163/10") show the earlier innings as a small line above the latest one, so the score stays one innings wide. */
const Score = ({ score, overs }: { score: string; overs?: string }) => {
  const parts = score.split(/\s*&\s*/);
  const last = parts.pop();
  return (
    <>
      {parts.length > 0 && <span className="pi">{parts.join(" & ")} &amp;</span>}
      <b>{last}</b>{overs && <small>{overs.split("/")[0]} ov</small>}
    </>
  );
};

const TeamRow = ({ t, m }: { t: TeamVm; m: Card }) => (
  <div className={`tr ${m.bucket === "past" && t.lost ? "l" : ""}`}>
    <Crest t={t} />
    <div className="nm"><span>{t.name}</span>{m.bucket === "past" && t.won && <span className="w" title="Won"><IcChk /></span>}{m.bucket === "live" && t.batting && <span className="bat">BATTING</span>}</div>
    <div className="sc">{m.bucket === "up" ? null : t.score ? <Score score={t.score} overs={t.overs} /> : <span className="dnb">{m.bucket === "live" ? "Yet to bat" : "Did not bat"}</span>}</div>
  </div>
);

/** Ticks once a second while any upcoming card is on screen. */
const useNow = (on: boolean) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!on) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [on]);
  return now;
};

const p2 = (n: number) => String(n).padStart(2, "0");
const dayDiff = (d: Date, now: number) => {
  const n = new Date(now);
  return Math.round((new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() - new Date(n.getFullYear(), n.getMonth(), n.getDate()).getTime()) / 86400000);
};
const whenTag = (d: Date | null, now: number) => {
  if (!d) return "TBA";
  const dd = dayDiff(d, now);
  return dd === 0 ? "TODAY" : dd === 1 ? "TOMORROW" : d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }).toUpperCase();
};

/** Toss is 30 minutes before the start; shown in the upcoming card's footer. */
const tossLine = (m: Card) => m.raw.tossResult
  ? <span className="ft-note ok" title={m.raw.tossResult}>{m.raw.tossResult}</span>
  : <span className="ft-note">{m.start ? `Toss ${new Date(m.start.getTime() - 30 * 60000).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}` : "Toss time to be announced"}</span>;

/** STARTS IN · DAYS HRS MINS SECS (same timer as the match page). */
const Countdown = ({ m, now }: { m: Card; now: number }) => {
  if (!m.start) return <div className="mc-res up">Start time to be announced</div>;
  const s = Math.max(0, Math.floor((m.start.getTime() - now) / 1000));
  const parts: [number, string][] = [[Math.floor(s / 86400), "DAYS"], [Math.floor((s % 86400) / 3600), "HRS"], [Math.floor((s % 3600) / 60), "MINS"], [s % 60, "SECS"]];
  return (
    <div className={`mc-cd ${s ? "" : "zero"}`} role="timer" aria-label={`Starts in ${parts.map(([v, u]) => `${v} ${u.toLowerCase()}`).join(" ")}`}>
      <div className="cdr"><span className="lbl">{s ? "STARTS IN" : "STARTING NOW"}</span><div className="cdu">{parts.map(([v, u]) => <div key={u}><b>{p2(v)}</b><span>{u}</span></div>)}</div></div>
    </div>
  );
};

const initials = (n: string) => n.split(" ").filter(Boolean).map((w) => w[0]).join("").slice(0, 2).toUpperCase();

const MatchCard = ({ m, now, onOpen }: { m: Card; now: number; onOpen: (m: Card) => void }) => {
  const when = m.bucket === "past" && m.start ? `${m.start.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}, ${m.time}` : m.time;
  const winner = m.a.won ? m.a : m.b.won ? m.b : null;
  return (
    <a className="mc" href={`/matches-details/${m.id}`} onClick={(e) => { e.preventDefault(); onOpen(m); }} style={{ ["--ca" as any]: m.a.color, ["--cb" as any]: m.b.color }}
      aria-label={`${m.stage ?? ""} ${m.tournamentName}, ${m.a.name} versus ${m.b.name}`.trim()}>
      <div className="mc-tn"><span className="tn"><IcTrS />{m.tournamentName}</span>{m.format && <span className="fm2">{m.format}</span>}</div>
      <div className="mc-top">
        {m.stage && <span className="stg">{m.stage}</span>}
        {m.stage && when && <span className="dot" />}
        {when && <span>{when}</span>}
        {m.ground && <span className="gr" title={m.ground}><IcPin /><span>{m.ground}</span></span>}
        {m.bucket === "live" ? <span className="tag live"><span className="ldot on" />LIVE</span> : m.bucket === "past" ? <span className="tag">RESULT</span> : <span className="tag">{whenTag(m.start, now)}</span>}
      </div>
      <div className="mc-teams"><TeamRow t={m.a} m={m} /><TeamRow t={m.b} m={m} /></div>
      {m.bucket === "past" && <div className={`mc-res ${m.outcome === "W" ? "" : "nr"}`}>{m.resultTitle || "Result to be confirmed"}</div>}
      {m.bucket === "live" && <div className="mc-res lv">{m.resultTitle && m.resultTitle !== "-" ? m.resultTitle : "Match in progress"}</div>}
      {m.bucket === "up" && <Countdown m={m} now={now} />}
      <div className="mc-ft">
        {m.bucket === "past" && (m.potm
          ? <div className="pm"><span className="av" style={{ background: winner ? `${winner.color}1A` : "#EEEEF7", color: winner?.color ?? "#29276B" }}>{initials(m.potm)}</span><span className="pmt"><span className="k">PLAYER OF THE MATCH</span><span className="v" title={m.potm}>{m.potm}</span></span></div>
          : <span className="ft-note">No player of the match</span>)}
        {m.bucket === "live" && (m.hasLiveStream ? <span className="rem"><IcPlay />Watch live</span> : <span className="ft-note">Ball-by-ball updates</span>)}
        {m.bucket === "up" && tossLine(m)}
        <span className="go" aria-hidden="true"><span className="gt">{m.bucket === "past" ? "Match centre" : m.bucket === "live" ? "Live match" : "Match preview"}</span><IcArr /></span>
      </div>
    </a>
  );
};

const Skeleton = () => (
  <div className="crt" style={{ overflow: "hidden" }}>
    {[0, 1, 2].map((i) => (
      <div key={i} className="skc" style={{ padding: 18, gap: 14 }}>
        <span className="sk" style={{ height: 12, width: "55%" }} /><span className="sk" style={{ height: 34 }} /><span className="sk" style={{ height: 34 }} /><span className="sk" style={{ height: 40 }} />
      </div>
    ))}
  </div>
);

const LeagueMatches = ({ leagueId, leagueName }: { leagueId?: string | null; leagueName?: string }) => {
  const nav = useNavigate();
  const dispatch = useAppDispatch();
  const { live, up, past, morePast, loading, error, refetch } = useLeagueMatches(leagueId);

  const cards = useMemo(() => {
    const L = live.map((r) => toCard(r, "live")).sort((a, b) => a.ms - b.ms);
    const U = up.map((r) => toCard(r, "up")).sort((a, b) => (a.ms || Infinity) - (b.ms || Infinity));
    const P = past.map((r) => toCard(r, "past")).sort((a, b) => b.ms - a.ms).slice(0, MAX_RESULTS);
    return { L, U, P, all: [...L, ...U, ...P] };
  }, [live, up, past]);

  const now = useNow(cards.U.length > 0);
  const track = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState({ start: true, end: true });
  const arrows = useCallback(() => {
    const t = track.current;
    if (!t) return;
    setEdge({ start: t.scrollLeft <= 4, end: t.scrollLeft + t.clientWidth >= t.scrollWidth - 4 });
  }, []);
  useEffect(() => {
    arrows();
    addEventListener("resize", arrows);
    return () => removeEventListener("resize", arrows);
  }, [arrows, cards.all.length]);
  const slide = (d: number) => {
    const t = track.current, c = t?.children[0] as HTMLElement | undefined;
    if (!t || !c) return;
    const w = c.getBoundingClientRect().width + 18;
    t.scrollBy({ left: d * w * Math.max(1, Math.floor(t.clientWidth / w)), behavior: "smooth" });
  };

  const goTournaments = () => {
    dispatch(setActiveTab("tournaments"));
    sessionStorage.setItem("activeTab", "tournaments");
    window.scrollTo({ top: 0 });
  };
  const open = (m: Card) => nav(`/matches-details/${m.id}`);

  const counts = [
    cards.L.length ? <span key="l" className="lv"><span className="ldot on" />{cards.L.length} live</span> : null,
    cards.U.length ? <span key="u">{cards.U.length}{up.length >= 20 ? "+" : ""} upcoming</span> : null,
    cards.P.length ? <span key="p">{cards.P.length}{morePast ? "+" : ""} {morePast ? "recent results" : cards.P.length === 1 ? "result" : "results"}</span> : null,
  ].filter(Boolean);

  return (
    <section className="lsec" id="secM" aria-labelledby="hM">
      <div className="lsh">
        <h2 id="hM">Matches</h2>
        {!loading && !error && cards.all.length > 0 && (
          <div className="r">
            <span className="mcount">{counts.flatMap((c, i) => (i ? [<i key={`s${i}`} />, c] : [c]))}</span>
            <span className="hnav">
              <button className="cnav2 prev" aria-label="Previous matches" disabled={edge.start} onClick={() => slide(-1)}><IcPrev /></button>
              <button className="cnav2 next" aria-label="More matches" disabled={edge.end} onClick={() => slide(1)}><IcNext /></button>
            </span>
          </div>
        )}
      </div>
      {loading ? <Skeleton /> : error ? (
        <div className="empty" style={{ marginTop: 0 }}><span className="ic"><IcLive /></span><h3>Couldn't load matches</h3><p>Check your connection and try again.</p><div className="acts"><button className="btn primary" onClick={refetch}>Try again</button></div></div>
      ) : cards.all.length === 0 ? (
        <div className="empty" style={{ marginTop: 0 }}><span className="ic"><IcCalL /></span><h3>No matches yet</h3><p>Live scores, upcoming fixtures and results from every {leagueName || "league"} tournament will show here once the first fixtures are published.</p><div className="acts"><button className="btn primary" onClick={goTournaments}>See tournaments</button></div></div>
      ) : (
        <div className="crsl">
          <button className="cnav prev" aria-label="Previous matches" disabled={edge.start} onClick={() => slide(-1)}><IcPrev /></button>
          <div className="crt" ref={track} onScroll={arrows}>
            {cards.all.map((m) => <MatchCard key={m.id} m={m} now={now} onOpen={open} />)}
            {cards.P.length > 0 && (
              <a className="mtile" href="#" onClick={(e) => { e.preventDefault(); goTournaments(); }}>
                <span className="ic2"><IcTrophy /></span>
                <b>See every result</b>
                <span>Every result, grouped by tournament. Every season is on the Tournaments page.</span>
                <span className="alink">Go to Tournaments<IcArr /></span>
              </a>
            )}
          </div>
          <button className="cnav next" aria-label="More matches" disabled={edge.end} onClick={() => slide(1)}><IcNext /></button>
        </div>
      )}
    </section>
  );
};

export default LeagueMatches;
